<template>
  <b-container fluid class="parameters-table">
    <div class="parameters-table__left">
      <b-row class="parameters-table__left-header m-0">
        <b-col
          class="parameters-table__col parameters-table__category p-0"
          cols="12"
        >
          <strong>
            {{ $t('engineering.physical-chemical.parameters-tab.category') }}
          </strong>
          <span>
            {{ tableCategory }}
          </span>
        </b-col>
        <b-col
          class="parameters-table__col parameters-table__title p-0"
        >
          {{ $t('engineering.reports.test') }}
        </b-col>
        <b-col
          v-if="showValues"
          class="
            parameters-table__value
            parameters-table__gray
            parameters-table__title
          "
          cols="3"
        >
          {{ $t('general.value') }}
        </b-col>
      </b-row>
      <b-row
        v-for="item in table.tableVertical"
        class="parameters-table__col m-0"
        :key="item.key"
      >
        <b-col align-v="center">
          <b-row>
            <b-col
              class="parameters-table__text"
              cols="auto"
            >
              <span>
                {{ $t(item.text, item.textParams) }}
              </span>
              <info-badge
                v-if="item.tooltip"
                :translation-key="item.tooltip"
              >
              </info-badge>
            </b-col>
          </b-row>
        </b-col>
        <b-col
          v-if="showValues"
          class="
            p-0
            parameters-table__gray
            parameters-table__value
          "
          cols="3"
        >
          <span class="parameters-table__text">
            {{ item.value }}
          </span>
        </b-col>
      </b-row>
    </div>
    <div class="parameters-table__right-wrapper">
      <b-row
        class="parameters-table__right m-0"
      >
        <b-col
          v-for="(column, index) in parameters"
          :key="index"
          cols="auto"
        >
          <b-row class="parameters-table__dropdown-wrapper">
            <b-col
              class="parameters-table_enabled p-0 parameters-table__ellipsis-btn"
            >
              <b-dropdown
                v-show="!isEditing"
                variant="link"
                size="lg"
                toggle-class="text-decoration-none"
                no-caret
                right
                :disabled="hasntUserPermission"
              >
                <template slot="button-content"></template>
                <b-dropdown-item
                  aria-hidden="true"
                  href="#"
                  @click="hideLots(column)"
                >
                  <i
                    aria-hidden="true"
                    :class="toggleLotsVisibilityIcon(column)"
                  />
                  <span> {{ toggleLotsVisibilityLabel(column) }} </span>
                </b-dropdown-item>
                <b-dropdown-item
                  aria-hidden="true"
                  href="#"
                  :disabled="disableAddCustom()"
                  @click="addCustomValue(column, index)"
                >
                  <i
                    class="fas fa-plus"
                    aria-hidden="true"
                  />
                  <span>
                        {{ $t('engineering.physical-chemical.parameters-tab.add-custom') }}
                      </span>
                  <info-badge
                    v-if="disableAddCustom()"
                    class="parameters-table__add-custom-tooltip"
                    :string-key="$t('messages.MI013.message', { customParameters })"
                  />
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="column.custom"
                  aria-hidden="true"
                  href="#"
                  @click="editCustomValue(column, index)"
                >
                  <i
                    class="fas fa-pencil-alt"
                    aria-hidden="true"
                  />
                  <span>
                    {{ $t('engineering.physical-chemical.parameters-tab.edit-custom') }}
                  </span>
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="column.custom"
                  aria-hidden="true"
                  href="#"
                  @click="deleteCustomValue(index)"
                >
                  <i
                    class="fas fa-trash-alt text-red"
                    aria-hidden="true"
                  />
                  <span class="text-red">
                        {{ $t('engineering.physical-chemical.parameters-tab.delete-custom') }}
                      </span>
                </b-dropdown-item>
              </b-dropdown>
            </b-col>
          </b-row>
          <b-row class="parameters-table__header">
            <b-col>
              <b-row
                class="
                  parameters-table__title
                  parameters-table__gray
                "
                align-h="center"
                align-v="center"
              >
                <b-col
                  class="parameters-table__align-center"
                  :class="{ 'parameters-table_disabled': !column.enabled }"
                >
                  <s-input-label-edit
                    class="text-primary text-center parameters-table__label-edit"
                    :class="{ 'parameters-table__custom font-weight-normal': column.custom }"
                    :editable="isBeingEdited(index)"
                    :input-options="{
                      maxLength: 25,
                      placeholder: $t('engineering.physical-chemical.parameters-tab.enter-name'),
                    }"
                    v-model.trim="column.name"
                  />
                </b-col>
                <b-col
                  class="parameters-table_enabled p-0 parameters-table__ellipsis-btn"
                >
                  <b-dropdown
                    v-show="!isEditing"
                    class="parameters-table__no-content-dropdown"
                    variant="link"
                    size="lg"
                    toggle-class="text-decoration-none"
                    no-caret
                    right
                    :disabled="hasntUserPermission"
                  >
                    <template slot="button-content">
                      <i
                        aria-hidden="true"
                        class="fa fa-ellipsis-v"
                      />
                    </template>
                  </b-dropdown>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
          <b-row :class="{ 'parameters-table_disabled': !column.enabled }">
            <b-col
              v-for="(rule, rulesIndex) in column.rules"
              :cols="columnRulesCol(column)"
              :key="rule.id"
            >
              <b-row
                class="parameters-table__row parameters-table__diagnostic-rule"
                align-h="center"
                align-v="center"
                :class="{ 'parameters-table__custom': column.custom }"
              >
                <label class="text-primary">
                  {{ diagnosticRuleLabel(rule.diagnosticRule) }}
                </label>
              </b-row>
              <b-row
                class="parameters-table__row text-primary font-weight-bold"
                v-for="test in fieldsOptions"
                align-h="center"
                align-v="center"
                :class="{ 'parameters-table__custom': column.custom }"
                :key="test.key"
              >
                <s-input-label-edit
                  v-model="rule[test.key]"
                  :formatted-value="formatNumberValue(rule[test.key], test.precision)"
                  :editable="editMode(rule, test.key, index)"
                >
                  <div class="parameters-table__field text-center">
                    <s-number-input
                      v-model="rule[test.key]"
                      :numeric-options="configureNumericOptions(test)"
                      :invalid="validationProxy(test, column.id, rulesIndex)"
                      :disabled="isDisabledField(test.key, column.id, rulesIndex)"
                      @input="onInputValue($event, test, column.id, rulesIndex)"
                    />
                    <b-form-invalid-feedback id="input-live-feedback">
                      {{ getValidationMessage(test, column.id, rulesIndex) }}
                    </b-form-invalid-feedback>
                  </div>
                </s-input-label-edit>
              </b-row>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import InfoBadge from '@/components/icon/InfoBadge';
import { Norms, SampleType, diagnosticRule } from 'sigma-mappings';
import {
  getOperatorLabel,
  compareValuesByOperator,
} from '@/services/stringHelper';

export default {
  name: 'ParametersTable',
  components: {
    InfoBadge,
  },
  props: {
    table: {
      type: Object,
      default: () => ({ tableHorizontal: {} }),
    },
    parameters: {
      type: Array,
      default: () => ([]),
    },
    showCategory: {
      type: Boolean,
      default: false,
    },
    showValues: {
      type: Boolean,
      default: false,
    },
    columnInserting: {
      type: Number,
      default: null,
    },
    columnEditing: {
      type: Number,
      default: null,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters([
      'limitOfPhysicalChemicalParametersCustomizations',
    ]),
    customParameters() {
      return this.limitOfPhysicalChemicalParametersCustomizations;
    },
    tableCategory() {
      const { category } = this.table.tableHorizontal || {};
      return category ? category.description : '';
    },
    isEditingOrInserting() {
      return this.columnInserting || this.columnEditing;
    },
    hasntUserPermission() {
      return !this.$hasUserPermission('PER013');
    },
    columnsTableVertical() {
      return this.table.tableVertical.map(({ text }) => text);
    },
    fieldsOptions() {
      return this.table.tableVertical;
    },
    testFields() {
      return this.table.tableVertical.map(({ key }) => key);
    },
    isIecTypeUsed() {
      const { norm, sampleType } = this.table.tableHorizontal || {};
      return norm === Norms.IEC_60422_2013 && sampleType === SampleType.USED;
    },
  },
  methods: {
    isOriginalInhibitorContentFromHarmful(model, rule) {
      return model === 'originalInhibitorContent'
        && rule.diagnosticRule === diagnosticRule.HARMFUL;
    },
    isOriginalInhibitorContentFromUnsatisfactory(model, rule) {
      return model === 'originalInhibitorContent'
        && rule.diagnosticRule === diagnosticRule.UNSATISFACTORY;
    },
    getFieldKey(model, rule) {
      return this.isOriginalInhibitorContentFromHarmful(model, rule)
        ? rule[model]
        : model;
    },
    isDisabledField(model, parameterId, ruleIndex) {
      const rule = this.findRule(ruleIndex, parameterId);
      return this.isIecTypeUsed
        && (this.isOriginalInhibitorContentFromHarmful(model, rule)
          || this.isOriginalInhibitorContentFromUnsatisfactory(model, rule));
    },
    onInputValue(value, test, parameterId, ruleIndex) {
      const inputRule = this.findRule(ruleIndex, parameterId);
      if (this.isIecTypeUsed && test.key === 'originalInhibitorContent' && inputRule.diagnosticRule === diagnosticRule.UNSATISFACTORY) {
        const rule = this.getEditingRule(diagnosticRule.HARMFUL);
        this.$set(rule, 'originalInhibitorContent', value);
      }
    },
    formatNumberValue(value, decimais = 0) {
      if (typeof value !== 'number') {
        return value;
      }
      return this.$n(value, {
        minimumFractionDigits: decimais,
        maximumFractionDigits: decimais,
      });
    },
    findRule(index, id = '') {
      const valueProxy = this.parameters.find(it => it.id === id) || {};
      return (valueProxy.rules || [])[index];
    },
    getRangeValidation({ minimum, maximum }, model, parameterId, ruleIndex) {
      const rule = this.findRule(ruleIndex, parameterId);
      if (!rule || rule[model] == null) {
        return null;
      }
      const ruleModel = rule[model];
      return (ruleModel >= minimum) && (ruleModel <= maximum);
    },
    getEditingRule(ruleToFind) {
      const parameter = this.parameters[this.isEditingOrInserting] || { rules: [] };
      return parameter.rules.find(rule => rule.diagnosticRule === ruleToFind) || {};
    },
    getIecRuleValidation(model) {
      if (!this.isIecTypeUsed || !model.iecRuleOperator) return true;
      const unsatisfactoryRule = this.getEditingRule(diagnosticRule.UNSATISFACTORY);
      const harmfulRule = this.getEditingRule(diagnosticRule.HARMFUL);
      return compareValuesByOperator(
        unsatisfactoryRule[model.key],
        harmfulRule[model.key],
        model.iecRuleOperator,
      );
    },
    getIecRulesValidationMessage(ruleIndex, parameterId, model) {
      const rule = this.findRule(ruleIndex, parameterId);
      if (rule.diagnosticRule !== diagnosticRule.UNSATISFACTORY) return '';
      return this.$t('messages.ME065.message', {
        0: getOperatorLabel(model.iecRuleOperator),
        1: this.diagnosticRuleLabel(diagnosticRule.HARMFUL).toLowerCase(),
      });
    },
    getValidationMessage(model, parameterId, ruleIndex) {
      const rule = this.findRule(ruleIndex, parameterId);
      if (!this.isEditingOrInserting || this.isOriginalInhibitorContentFromHarmful(model.key, rule)) return '';
      const iecRulesValidation = this.getIecRuleValidation(model, parameterId, ruleIndex);
      if (!iecRulesValidation) {
        return this.getIecRulesValidationMessage(ruleIndex, parameterId, model);
      }
      const rangeValidation = this.getRangeValidation(
        model.range,
        model.key,
        parameterId,
        ruleIndex,
      );

      if (!rangeValidation) {
        return this.$t('messages.MA086.message', {
          minValue: this.formatNumberValue(model.range.minimum, model.precision),
          maxValue: this.formatNumberValue(model.range.maximum, model.precision),
        });
      }
      return '';
    },
    validationProxy(model, parameterId, ruleIndex) {
      if (!this.isEditingOrInserting) return false;
      return this.getValidationMessage(model, parameterId, ruleIndex) !== '';
    },
    configureNumericOptions(test) {
      return {
        decimalPlaces: test.precision,
        minimumValue: test.range.minimum,
        maximumValue: test.range.maximum,
      };
    },
    editMode(rule, key, index) {
      return this.isBeingEdited(index) && rule[key] !== undefined;
    },
    showTestLabelEdit(rule, test, index) {
      return rule[test] && this.isBeingEdited(index);
    },
    columnRulesCol(column) {
      return column.rules.length === 2 ? 6 : 12;
    },
    isBeingEdited(columnIndex) {
      return this.columnInserting === columnIndex || this.columnEditing === columnIndex;
    },
    scrollTableToColumn(columnIndex) {
      const parametersTable = document.body.getElementsByClassName('parameters-table')[0] || {};
      const columnSize = 420;
      const scrollDistance = columnSize * columnIndex;
      this.$nextTick(() => {
        parametersTable.scrollTo(scrollDistance, 0);
      });
    },
    addCustomValue(column, columnIndex) {
      this.$emit('add-custom', column, columnIndex + 1);
      this.scrollTableToColumn(columnIndex + 1);
    },
    editCustomValue(column, columnIndex) {
      this.$emit('edit-custom', column, columnIndex);
    },
    deleteCustomValue(columnIndex) {
      this.$emit('delete-custom', columnIndex);
    },
    disableAddCustom() {
      if (!this.parameters) return false;
      const customParameters = this.table.tableHorizontal.parameters
        .filter(parameter => parameter.custom);
      return customParameters.length >= this.customParameters;
    },
    toggleLotsVisibilityLabel({ enabled }) {
      return enabled
        ? this.$t('engineering.hide-lots')
        : this.$t('engineering.show-lots');
    },
    toggleLotsVisibilityIcon({ enabled }) {
      return enabled
        ? 'fas fa-eye-slash'
        : 'fas fa-eye';
    },
    hideLots(norm) {
      const code = norm.enabled ? 'MC033' : 'MC011';
      this.$confirmMessage({ code })
        .accepted(() => this.$emit('hide-lots', norm));
    },
    diagnosticRuleLabel(diagnostic) {
      const rulesLabel = {
        [diagnosticRule.UNDEFINED]: this.$t('enums.engineering.diagnostic-rule.undefined'),
        [diagnosticRule.STANDARD]: this.$t('enums.engineering.diagnostic-rule.standard'),
        [diagnosticRule.UNSATISFACTORY]: this.$t('enums.engineering.diagnostic-rule.unsatisfactory'),
        [diagnosticRule.HARMFUL]: this.$t('enums.engineering.diagnostic-rule.harmful'),
      };
      return rulesLabel[diagnostic];
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"

$cell-height: 95px

.parameters-table
  overflow: auto
  height: 600px
  position: relative
  padding-left: 0
  scroll-behavior: smooth

  &__field
    display: flex
    flex-direction: column
    justify-content: flex-end
    align-items: center

  &__value-edit
    max-width: 150px

  &__label-edit
    width: 80%

  &__add-custom-tooltip
    pointer-events: all

  &__custom, &__custom label
    color: var(--font-color-primary) !important

  &__category
    flex-direction: column
    text-align: center
    border-bottom: 1px solid white
    background-color: var(--color-table-header-bg) !important
    max-width: 300px

  &_disabled
    opacity: .5

    .parameters-table__diagnostic-rule.parameters-table__row
      border-bottom: 1px solid $color-gray-intermediary

  &_enabled
    opacity: 1 !important

  &__align-center
    display: flex
    align-items: center
    justify-content: center
    width: 100%

  &__row
    padding: 6px
    border: 1px solid white
    background-color: var(--color-table-body-accent-bg)
    max-width: 325px

    &:nth-child(2n)
      background-color: var(--color-table-header-bg)

  &__col
    max-width: 380px
    border-left: 1px solid white
    display: flex
    align-items: center
    justify-content: center
    background-color: var(--color-table-body-accent-bg)

    &:nth-child(2n)
      background-color: var(--color-table-body-accent-bg)

  &__value
    min-height: 90px
    border: 1px solid white
    display: flex
    align-items: center
    justify-content: center

  &__gray
    background: var(--color-table-header-bg)

  &__title
    border: 1px solid white
    font-size: 16px
    font-weight: bold
    color: var(--font-color-primary)
    position: relative

    ::v-deep .dropdown-item
      padding-left: 5px !important

      i
        min-width: 35px
        text-align: center
        padding-right: 5px

  &__ellipsis-btn
    position: absolute
    right: 30px
    width: 20px

    ::v-deep
      .dropdown
        height: 45px

      .dropdown-toggle
        z-index: 2

      .dropdown-menu
        z-index: 4

  &__dropdown-wrapper
    position: sticky
    top: 15px
    z-index: 7

  &__no-content-dropdown
    ::v-deep
    .dropdown-menu
      display: none

  &__sub-title
    font-size: 16px
    font-weight: normal
    font-style: italic
    color: var(--font-color-primary)

  &__text
    font-size: 1rem
    color: var(--font-color-primary)
    font-weight: 600

  &__alert-icon
    border: 1px solid
    padding: 3px
    font-size: 12px
    border-radius: 4px

  &__diagnostic-rule.parameters-table__row
    border-bottom: 1px solid $color-border-gray

  &__diagnostic-rule
    position: sticky
    top: $cell-height + 1

  &__left-header
    max-width: 300px
    position: sticky
    top: 0
    left: 0
    z-index: 6
    border-top: 1px solid white

    div:last-child
      border-bottom: 1px solid $color-border-gray

  &__header
    position: sticky
    top: 0
    z-index: 1
    min-width: 250px
    border-top: 1px solid white

  &__left
    width: 300px
    display: inline-flex
    flex-direction: column
    border-right: 1px solid white
    border-bottom: 1px solid $color-border-gray
    z-index: 3

    .parameters-table__col:not(.parameters-table__title):not(.parameters-table__category)
      border-left: 1px solid $color-border-gray
      z-index: 5

    +media-min-md
      position: sticky
      left: 0
      top: 0
      z-index: 3

  &__right-wrapper
    position: absolute
    top: 0
    display: inline-flex
    +media-xs
      margin-left: 300px

  &__right
    flex-wrap: nowrap
    flex-direction: row

    .parameters-table__title
      border-top: none

    > div
      border-bottom: 1px solid $color-border-gray

    div:last-child div:last-child
      div:last-child .parameters-table__row:not(.parameters-table__diagnostic-rule)
        border-right: 1px solid $color-border-gray

    div:last-child .parameters-table_disabled
      div:last-child .parameters-table__row:not(.parameters-table__diagnostic-rule)
        border-right: 1px solid $color-gray-intermediary !important

  &__row,
  &__col,
  &__value,
  &__title
    min-height: $cell-height
    max-height: $cell-height

.manage-fonts_16px
  $cell-height: 115px

  .parameters-table
    &__row
      max-width: 395px

    &__row,
    &__col,
    &__value,
    &__title
      min-height: $cell-height
      max-height: $cell-height

.manage-fonts_18px
  $cell-height: 125px

  .parameters-table
    &__row
      max-width: 410px

    &__row:not(.parameters-table__diagnostic-rule),
    &__col:not(.parameters-table__category):not(.parameters-table__title),
    &__value
      min-height: $cell-height
      max-height: $cell-height

</style>
